import { Injectable } from "@angular/core";
import { NotificationService } from "@flowtribe/ui";

/**
 * NotificationConfiguration interface from @flowtribe/ui
 */
export interface NotificationConfiguration {
  type: "success" | "warning" | "error" | "info";
  title: string;
  isDetailPresent: boolean;
  detail?: string;
  /** Rendered right after linkText, for sentences where the highlighted word isn't at the end. */
  detailSuffix?: string;
  detailLink?: string;
  linkText?: string;
  linkCallback?: () => void;
  sticky?: boolean;
}

/**
 * Enhanced notification service wrapper for @flowtribe/ui's NotificationService
 * Supports all notification types with optional details, links, and sticky behavior
 */
@Injectable()
export class NotifyService {
  constructor(private readonly notificationService: NotificationService) {}

  /**
   * Show a success notification
   * @param message - The main notification message
   * @param options - Optional configuration for details, links, and sticky behavior
   */
  success(message: string, options?: Partial<Omit<NotificationConfiguration, "type" | "title">>) {
    this.notificationService.showNotification({
      type: "success",
      title: message,
      isDetailPresent: !!options?.detail,
      ...options
    });
  }

  /**
   * Show a warning notification
   * @param message - The main notification message
   * @param options - Optional configuration for details, links, and sticky behavior
   */
  warning(message: string, options?: Partial<Omit<NotificationConfiguration, "type" | "title">>) {
    this.notificationService.showNotification({
      type: "warning",
      title: message,
      isDetailPresent: !!options?.detail,
      ...options
    });
  }

  /**
   * Show an info notification
   * @param message - The main notification message
   * @param options - Optional configuration for details, links, and sticky behavior
   */
  info(message: string, options?: Partial<Omit<NotificationConfiguration, "type" | "title">>) {
    this.notificationService.showNotification({
      type: "info",
      title: message,
      isDetailPresent: !!options?.detail,
      ...options
    });
  }

  /**
   * Show an error notification
   * @param message - The main notification message
   * @param options - Optional configuration for details, links, and sticky behavior
   */
  error(message: string, options?: Partial<Omit<NotificationConfiguration, "type" | "title">>) {
    this.notificationService.showNotification({
      type: "error",
      title: message,
      isDetailPresent: !!options?.detail,
      ...options
    });
  }

  /**
   * Show a custom notification with full control over all properties
   * @param config - Complete notification configuration
   */
  custom(config: Partial<NotificationConfiguration>) {
    this.notificationService.showNotification({
      isDetailPresent: !!config.detail,
      ...config
    });
  }

  /**
   * Remove a notification by index
   * @param index - The index of the notification to remove (default: 0 for the first notification)
   */
  clear(index: number = 0) {
    this.notificationService.removeNotification(index);
  }

  /**
   * Remove every notification matching a message, regardless of its position in the queue.
   *
   * `clear()` always drops index 0, which is only ever the intended toast when nothing else is on
   * screen. Anything queued ahead of it gets removed instead, and the intended one survives — fatal
   * for sticky notifications, which never auto-expire and so stay on screen forever. Removing all
   * matches also clears any duplicates stranded by an earlier mismatched clear.
   * @param message - The title the notification was shown with
   * @param linkText - Optional link text to match as well, for when several notifications share a title and only
   * the one belonging to a specific subject should go away.
   */
  clearByMessage(message: string, linkText?: string) {
    // The queue only ever holds a handful of toasts; the bound is just a guard against a
    // removeNotification that doesn't shrink the list, so this can never spin.
    for (let attempt = 0; attempt < 20; attempt++) {
      const index = this.findNotificationIndex(message, linkText);
      if (index < 0) return;
      this.notificationService.removeNotification(index);
    }
  }

  private findNotificationIndex(message: string, linkText?: string): number {
    let index = -1;
    // notification$ is BehaviorSubject-backed, so this callback runs synchronously.
    this.notificationService.notification$
      .subscribe(notifications => {
        index = notifications.findIndex(
          notification => notification.title === message && (linkText === undefined || notification.linkText === linkText)
        );
      })
      .unsubscribe();
    return index;
  }

  /**
   * Show a success notification with additional details
   * @param message - The main notification message
   * @param detail - Additional detail text
   */
  successWithDetail(message: string, detail: string) {
    this.success(message, { detail });
  }

  /**
   * Show an error notification with additional details
   * @param message - The main notification message
   * @param detail - Additional detail text
   */
  errorWithDetail(message: string, detail: string) {
    this.error(message, { detail });
  }

  /**
   * Show a warning notification with additional details
   * @param message - The main notification message
   * @param detail - Additional detail text
   */
  warningWithDetail(message: string, detail: string) {
    this.warning(message, { detail });
  }

  /**
   * Show an info notification with additional details
   * @param message - The main notification message
   * @param detail - Additional detail text
   */
  infoWithDetail(message: string, detail: string) {
    this.info(message, { detail });
  }

  /**
   * Show a notification with a clickable link
   * @param type - Notification type
   * @param message - The main notification message
   * @param linkText - Text for the clickable link
   * @param linkCallback - Function to execute when link is clicked
   */
  withLink(type: "success" | "warning" | "error" | "info", message: string, linkText: string, linkCallback: () => void) {
    this.notificationService.showNotification({
      type,
      title: message,
      isDetailPresent: false,
      linkText,
      linkCallback
    });
  }

  /**
   * Show a sticky notification that doesn't auto-dismiss
   * @param type - Notification type
   * @param message - The main notification message
   * @param detail - Optional additional detail text
   */
  sticky(type: "success" | "warning" | "error" | "info", message: string, detail?: string) {
    this.notificationService.showNotification({
      type,
      title: message,
      isDetailPresent: !!detail,
      detail,
      sticky: true
    });
  }
}
